import { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Upload, Trash2, ImagePlus, Loader2, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PHOTO_ROOM_LABELS } from '../../lib/listingConstants';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PhotoThumb = ({ photo, isCover, onDelete, onUpdateMeta }) => {
  const src = photo.signed_token ? `${API}/api/files/${photo.signed_token}` : null;
  return (
    <div
      className="relative bg-slate-100 rounded-xl overflow-hidden border border-slate-200"
      data-testid={`photo-thumb-${photo.file_id}`}
    >
      <div className="aspect-[4/3] w-full bg-slate-200">
        {src && <img src={src} alt={photo.caption || ''} className="w-full h-full object-cover" />}
      </div>
      {isCover && (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full">
          <Star className="h-3 w-3" /> Cover
        </span>
      )}
      <button
        type="button"
        onClick={onDelete}
        className="absolute top-2 right-2 h-7 w-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-white"
        data-testid={`delete-photo-${photo.file_id}`}
      >
        <Trash2 className="h-3.5 w-3.5 text-rose-600" />
      </button>
      <div className="p-2 space-y-1 bg-white">
        <select
          value={photo.room_label || ''}
          onChange={(e) => onUpdateMeta({ room_label: e.target.value })}
          className="w-full text-xs px-2 py-1 border border-slate-200 rounded-md bg-white"
          data-testid={`room-label-${photo.file_id}`}
        >
          <option value="">Untagged</option>
          {PHOTO_ROOM_LABELS.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
        <input
          type="text"
          value={photo.caption || ''}
          onChange={(e) => onUpdateMeta({ caption: e.target.value })}
          onBlur={(e) => onUpdateMeta({ caption: e.target.value })}
          placeholder="Caption (optional)"
          className="w-full text-xs px-2 py-1 border border-slate-200 rounded-md"
          data-testid={`caption-${photo.file_id}`}
        />
      </div>
    </div>
  );
};

const StepPhotos = ({ listing, refetch, onNext }) => {
  const { api } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = (listing.photos || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  const hasExterior = photos.some((p) => (p.room_label || '').toLowerCase() === 'exterior');
  const canContinue = photos.length >= 4 && hasExterior;

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name} is too big. Max 10 MB per photo.`);
          continue;
        }
        const form = new FormData();
        form.append('file', f);
        await api.post(`/listings/draft/${listing.listing_id}/photos`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await refetch();
      toast.success('Photos uploaded.');
    } catch (e) {
      toast.error("We couldn't upload one of those photos. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (file_id) => {
    try {
      await api.delete(`/listings/draft/${listing.listing_id}/photos/${file_id}`);
      await refetch();
    } catch (e) {
      toast.error("Couldn't delete that photo.");
    }
  };

  const onUpdateMeta = async (file_id, meta) => {
    try {
      await api.post(`/listings/draft/${listing.listing_id}/photos/${file_id}/meta`, meta);
      await refetch();
    } catch (e) {
      toast.error("Couldn't save photo details.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">Photos</h1>
        <p className="mt-1 text-sm text-slate-600">
          4–25 photos. The first one becomes the cover. <strong>At least one must be tagged "Exterior".</strong>
        </p>
      </div>

      <Card className="bg-amber-50 border-amber-200/60">
        <CardContent className="p-4 text-xs text-amber-900 leading-relaxed">
          We center-crop every photo to 4:3 and resize to max 2000 px wide. JPG, PNG, or WebP — up to 10 MB each.
        </CardContent>
      </Card>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => upload(e.target.files)}
        data-testid="photo-file-input"
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || photos.length >= 25}
        className="w-full h-14 btn-primary"
        data-testid="photo-upload-btn"
      >
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
        {uploading ? 'Uploading…' : `Upload photos (${photos.length}/25)`}
      </Button>

      {photos.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p, i) => (
            <PhotoThumb
              key={p.file_id}
              photo={p}
              isCover={i === 0}
              onDelete={() => onDelete(p.file_id)}
              onUpdateMeta={(meta) => onUpdateMeta(p.file_id, meta)}
            />
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="photo-empty-state">
          <Upload className="h-6 w-6 mx-auto mb-2 text-slate-400" />
          No photos yet.
        </div>
      )}

      {photos.length > 0 && !hasExterior && (
        <div className="px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700" data-testid="needs-exterior-warning">
          At least one photo needs the "Exterior" tag. Pick an outdoor shot and set its label.
        </div>
      )}

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue}
        onClick={onNext}
        data-testid="step-photos-continue"
      >
        Continue ({photos.length}/4 minimum)
      </Button>
    </div>
  );
};

export default StepPhotos;
